import { LOGOUT } from '../actions/auth';
import { GET_PRELOAD_DATA_FAIL, GET_PRELOAD_DATA_FETCH, GET_PRELOAD_DATA_SUCCESS } from '../actions/preload';
import {
  ADD_ANSWER,
  CHANGE_ANSWER,
  CHANGE_PAST_QUESTIONNAIRE_ANSWER,
  CHANGE_PAST_QUESTIONNAIRE_LIKE_STATE,
  GET_ELECTION_FAIL,
  GET_ELECTION_FETCH,
  GET_ELECTION_SUCCESS,
  GET_QUESTIONNAIRE_FAIL,
  GET_QUESTIONNAIRE_FETCH,
  GET_QUESTIONNAIRE_RESULTS_FAIL,
  GET_QUESTIONNAIRE_RESULTS_REQUEST,
  GET_QUESTIONNAIRE_RESULTS_SUCCESS,
  GET_QUESTIONNAIRE_SUCCESS,
  REVERT_LAST_QUESTION,
  SAVE_ANSWERS_TO_HASH,
  SAVE_QUESTIONNAIRE_RESULTS_FAIL,
  SAVE_QUESTIONNAIRE_RESULTS_REQUEST,
  SAVE_QUESTIONNAIRE_RESULTS_SUCCESS,
  SEND_ELECTION_ANSWERS_FAIL,
  SEND_ELECTION_ANSWERS_REQUEST,
  SEND_ELECTION_ANSWERS_SUCCESS,
  TOGGLE_ANSWER_LIKE
} from '../actions/questionnaires';

const normalizeQuestionnaires = questionnaires =>
  questionnaires.reduce((acc, cur) => {
    acc[cur.id] = cur;
    return acc;
  }, {});

const initializeQuestionnaireMeta = () => ({
  answers: {},
  likes: {},
  currentQuestion: 0,
  isOngoing: true
});

const initialState = {
  questionnaires: {},
  answersHash: localStorage.getItem('results') || {},
  currentQuestionnaire: null,
  currentElection: null,
  meta: {},
  results: [],
  currentElectionResult: null,
  loading: false,
  error: null,
  voteDate: null
};

const questionnaireReducerStrategy = {
  [GET_QUESTIONNAIRE_FETCH]: state => ({ ...state, currentQuestionnaire: null, loading: true }),
  [GET_QUESTIONNAIRE_SUCCESS]: (state, { questionnaire }) => ({
    ...state,
    currentQuestionnaire: questionnaire,
    loading: false
  }),
  [GET_QUESTIONNAIRE_FAIL]: (state, { message }) => ({
    ...state,
    error: message,
    currentQuestionnaire: null,
    loading: false
  }),
  [GET_ELECTION_FETCH]: state => ({ ...state, currentElection: null, loading: true }),
  [GET_ELECTION_SUCCESS]: (state, { election }) => ({
    ...state,
    currentElection: election,
    loading: false
  }),
  [GET_ELECTION_FAIL]: (state, { message }) => ({
    ...state,
    error: message,
    currentElection: null,
    loading: false
  }),
  [GET_PRELOAD_DATA_FETCH]: state => ({
    ...state,
    loading: true
  }),
  [GET_PRELOAD_DATA_SUCCESS]: (state, { voteDate, voting: { items } }) => ({
    ...state,
    questionnaires: normalizeQuestionnaires(items),
    loading: false,
    voteDate,
    meta: {
      ...state.meta,
      ...items
        .filter(({ id }) => !Object.keys(state.meta).includes(id))
        .reduce((acc, cur) => {
          acc[cur.id] = initializeQuestionnaireMeta();
          return acc;
        }, {})
    }
  }),
  [GET_PRELOAD_DATA_FAIL]: (state, { message }) => ({
    ...state,
    error: message,
    loading: false
  }),
  [GET_QUESTIONNAIRE_RESULTS_FAIL]: state => ({
    ...state,
    loading: false
  }),
  [GET_QUESTIONNAIRE_RESULTS_REQUEST]: state => ({
    ...state,
    loading: true
  }),
  [GET_QUESTIONNAIRE_RESULTS_SUCCESS]: (state, { results }) => {
    return {
      ...state,
      loading: false,
      results: results.map(it => ({
        ...it,
        content: {
          ...it.content,
          likes: Object.keys(it.content.likes || {}).reduce(
            (acc, cur) => ({
              ...acc,
              [cur]: Boolean(Number(it.content.likes[cur]))
            }),
            {}
          )
        }
      }))
    };
  },
  [SAVE_ANSWERS_TO_HASH]: (state, { hashId, answers }) => ({
    ...state,
    answersHash: {
      ...state.answersHash,
      [hashId]: answers
    }
  }),
  [SAVE_QUESTIONNAIRE_RESULTS_REQUEST]: state => ({
    ...state,
    loading: true
  }),
  [SAVE_QUESTIONNAIRE_RESULTS_SUCCESS]: state => {
    return {
      ...state,
      loading: false
    };
  },
  [SAVE_QUESTIONNAIRE_RESULTS_FAIL]: state => {
    return {
      ...state,
      loading: false
    };
  },
  [SEND_ELECTION_ANSWERS_REQUEST]: state => ({
    ...state,
    currentElectionResult: null,
    loading: true,
    error: null
  }),
  [SEND_ELECTION_ANSWERS_SUCCESS]: (state, { results }) => ({
    ...state,
    currentElectionResult: results,
    loading: false
  }),
  [SEND_ELECTION_ANSWERS_FAIL]: (state, { message }) => ({
    currentElectionResult: null,
    error: message,
    loading: false
  }),

  [CHANGE_PAST_QUESTIONNAIRE_ANSWER]: (state, { questionnaireId, questionId, answer }) => {
    const results = [...state.results];
    const questionnaireIndex = results.findIndex(it => String(it.votingProposal) === questionnaireId);
    const questionnaire = results[questionnaireIndex];
    questionnaire.content = {
      ...questionnaire.content,
      answers: {
        ...questionnaire.content.answers,
        [questionId]: answer
      }
    };
    results[questionnaireIndex] = questionnaire;
    return {
      ...state,
      results
    };
  },
  [CHANGE_PAST_QUESTIONNAIRE_LIKE_STATE]: (state, { questionnaireId, questionId }) => {
    const results = [...state.results];
    const questionnaireIndex = results.findIndex(it => String(it.votingProposal) === questionnaireId);
    const questionnaire = results[questionnaireIndex];

    const nextContent = {
      ...questionnaire.content,
      likes: {
        ...questionnaire.content.likes,
        [questionId]: !(questionnaire.content.likes && questionnaire.content.likes[questionId])
      }
    };

    const nextQuestionnaire = {
      ...questionnaire,
      content: nextContent
    };

    const nextResults = results.map((questionnaire, index) =>
      index === questionnaireIndex ? nextQuestionnaire : questionnaire
    );

    return {
      ...state,
      results: nextResults
    };
  },
  [ADD_ANSWER]: (state, { questionId, questionnaireId, answer }) => {
    const questionnaireMeta = state.meta[questionnaireId];
    const questionnaire = state.questionnaires[questionnaireId];
    const nextQuestion = questionnaireMeta.currentQuestion + 1;
    return {
      ...state,
      meta: {
        ...state.meta,
        [questionnaireId]: {
          ...questionnaireMeta,
          answers: {
            ...questionnaireMeta.answers,
            [questionId]: answer
          },
          isOngoing: questionnaire.questions.length !== nextQuestion,
          currentQuestion: nextQuestion
        }
      }
    };
  },
  [REVERT_LAST_QUESTION]: (state, { questionId, questionnaireId }) => {
    const questionnaireMeta = state.meta[questionnaireId];
    return {
      ...state,
      meta: {
        ...state.meta,
        [questionnaireId]: {
          ...questionnaireMeta,
          answers: {
            ...questionnaireMeta.answers,
            [questionId]: null
          },
          currentQuestion: questionnaireMeta.currentQuestion - 1
        }
      }
    };
  },
  [CHANGE_ANSWER]: (state, { questionnaireId, questionId, answer }) => {
    const questionnaireMeta = state.meta[questionnaireId];
    return {
      ...state,
      meta: {
        ...state.meta,
        [questionnaireId]: {
          ...questionnaireMeta,
          answers: {
            ...questionnaireMeta.answers,
            [questionId]: answer
          },
          currentQuestion: questionnaireMeta.currentQuestion + 1
        }
      }
    };
  },
  [TOGGLE_ANSWER_LIKE]: (state, { questionnaireId, questionId }) => {
    const questionnaireMeta = state.meta[questionnaireId];
    const likeStatus = questionnaireMeta.likes[questionId];
    return {
      ...state,
      meta: {
        ...state.meta,
        [questionnaireId]: {
          ...questionnaireMeta,
          likes: {
            ...questionnaireMeta.likes,
            [questionId]: !likeStatus
          }
        }
      }
    };
  },
  [LOGOUT]: state => {
    return {
      ...state,
      meta: Object.keys(state.meta).reduce((acc, cur) => {
        acc[cur] = initializeQuestionnaireMeta();
        return acc;
      }, {}),
      results: []
    };
  }
};

const questionnaireReducer = (state = initialState, action) => {
  const reducer = questionnaireReducerStrategy[action.type];
  return (reducer && reducer(state, action)) || state;
};

export default questionnaireReducer;
